import type { LocationQuery } from 'vue-router'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isUuidShaped(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

export function buildCanonicalWorkspacePath(
  organizationSlug: string,
  workspaceId: string,
  restPath: string,
): string {
  const rest = restPath.replace(/^\//, '')
  if (!rest) return '/' + organizationSlug + '/' + workspaceId
  return '/' + organizationSlug + '/' + workspaceId + '/' + rest
}

export type OrganizationSlugDecision =
  | { type: 'continue' }
  | { type: 'redirect'; path: string; query: LocationQuery }

export interface OrganizationSlugDecisionInput {
  path: string
  organizationSlug?: string
  workspaceId?: string
  query: LocationQuery
  /** Lookup wsid → organization slug; null when the user is not a member. */
  getOrganizationSlugForWorkspace: (workspaceId: string) => string | null
}

/**
 * Decide how to canonicalise an organization/workspace URL. Pure — no Nuxt/store side
 * effects. Belonging (organization/workspace membership → 404) is owned by the access
 * gate (~/middleware/05.access.global.ts); this function only issues redirects.
 */
export function decideOrganizationSlugAction(
  input: OrganizationSlugDecisionInput,
): OrganizationSlugDecision {
  const { path, workspaceId, query, getOrganizationSlugForWorkspace } = input
  let organizationSlug = input.organizationSlug

  const pathSegments = path.split('/').filter(Boolean)

  // No route matched (both params empty) — deep legacy path or garbage:
  // - Deep legacy `/1111-…/tests/xyz/edit` (UUID first segment) → treat
  //   the UUID as the organizationSlug param so the legacy case below handles it
  // - Anything else (`/random/stuff`) → continue (natural 404)
  if (!organizationSlug && !workspaceId) {
    if (!isUuidShaped(pathSegments[0])) return { type: 'continue' }
    organizationSlug = pathSegments[0]
  }

  // Legacy `/wsid/…` (UUID in the organizationSlug slot):
  // - Member workspace → redirect to `/:slug/:wsid/…`
  //   e.g. `/1111-…/simulations` → `/acme/1111-…/simulations`
  // - Non-member workspace → fall through (access gate 404s it)
  if (isUuidShaped(organizationSlug)) {
    // In this branch the "slug" is really a workspace id.
    const legacyWorkspaceId = organizationSlug
    const owningOrganizationSlug =
      getOrganizationSlugForWorkspace(legacyWorkspaceId)
    if (!owningOrganizationSlug) return { type: 'continue' }

    const rest = pathSegments.slice(1).join('/')
    const canonical = buildCanonicalWorkspacePath(
      owningOrganizationSlug,
      legacyWorkspaceId,
      rest,
    )
    // Safety check: if the "fixed" URL is the same as the current one,
    // redirecting would loop forever. Can only happen if an organization's slug
    // is itself a UUID (bad data).
    if (path === canonical) return { type: 'continue' }
    return { type: 'redirect', path: canonical, query }
  }

  // Organization-level page (`/:organizationSlug` or `/:organizationSlug/settings/…`) needs no
  // canonicalisation — belonging is enforced by the access gate. Fall through
  // to continue.

  // Workspace page (`/:organizationSlug/:wsid/…`):
  // - Stale `/wrong-slug/:wsid/…` (organization renamed, old bookmark) → redirect
  //   to the correct slug
  //   e.g. `/old-name/1111-…/tests` → `/acme/1111-…/tests`
  // - Non-member workspace → fall through (access gate 404s it)
  // - Correct slug (`/acme/1111-…/tests`) → continue
  if (organizationSlug && workspaceId) {
    const owningOrganizationSlug = getOrganizationSlugForWorkspace(workspaceId)
    if (!owningOrganizationSlug) return { type: 'continue' }

    if (owningOrganizationSlug !== organizationSlug) {
      const rest = pathSegments.slice(2).join('/')
      return {
        type: 'redirect',
        path: buildCanonicalWorkspacePath(
          owningOrganizationSlug,
          workspaceId,
          rest,
        ),
        query,
      }
    }
  }

  // Canonical URL → continue.
  return { type: 'continue' }
}
